/**
 * 用户端漫剧业务 HTTP 客户端（ThinkPHP index/UserApi）。
 * 与 `docs/功能文档.md` 中各步骤接口一一对应；新增 action 请在 userApi 对象内按「认证 / 会员 / 项目 / 步骤1~4 / 资产 / 支付」分组并写简短 JSDoc。
 */
/** 未配置 VUE_APP_API_BASE 时的默认后端域名（可被 .env.* 覆盖） */
const DEFAULT_API_HOST = {
  development: "http://localhost:9994",
  production: "https://www.ailintu.com"
};

function resolveApiHost() {
  const fromEnv = String(process.env.VUE_APP_API_BASE || "").trim();
  if (fromEnv) return fromEnv;
  const nodeEnv = process.env.NODE_ENV === "production" ? "production" : "development";
  return DEFAULT_API_HOST[nodeEnv] || DEFAULT_API_HOST.development;
}

function normalizeApiBase(rawBase) {
  // 开发环境统一走同源代理，彻底规避 CORS（目标地址见 vue.config.js devServer.proxy）。
  if (process.env.NODE_ENV === "development") {
    return "/index.php?s=index/UserApi";
  }
  const base = String(rawBase || resolveApiHost()).replace(/\/+$/, "");
  // 兼容 ThinkPHP query 路由形式，例如：
  // http://localhost:9994/index.php?s=index/UserApi
  if (/\/index\.php\?s=/i.test(base)) {
    return base;
  }
  // 兼容两种配置：
  // 1) 仅配置主机：http://localhost:9994
  // 2) 配置完整前缀：http://localhost:9994/api/user 或 .../index.php/api/user
  if (/\/(?:index\.php\/)?api\/user$/i.test(base)) {
    return base;
  }
  return `${base}/index.php?s=index/UserApi`;
}

const API_BASE = normalizeApiBase(resolveApiHost());

function request(path, options = {}) {
  const token = localStorage.getItem("user_token") || "";
  const headers = Object.assign(
    {},
    options.headers || {},
    token ? { Authorization: token, "X-Auth-Token": token } : {}
  );
  let body;
  if (options.body !== undefined) {
    if (options.formData) {
      body = options.body;
      // 注意：FormData 不能手动设置 Content-Type（包含 boundary），让浏览器自动处理
    } else
    if (options.form) {
      headers["Content-Type"] = "application/x-www-form-urlencoded;charset=UTF-8";
      body = new URLSearchParams(options.body).toString();
    } else {
      headers["Content-Type"] = "application/json";
      body = JSON.stringify(options.body);
    }
  }

  const action = String(path || "").replace(/^\/+/, "");
  let url;
  // ThinkPHP：`s` 中含多级路径时，部分网关/代理对未编码的 `/` 处理不一致，统一编码可避免 404
  const tpUserPrefix = "index/UserApi";
  if (
    /\/index\.php\?s=index\/UserApi$/i.test(API_BASE) ||
    API_BASE.endsWith("?s=index/UserApi") ||
    API_BASE.endsWith("&s=index/UserApi")
  ) {
    const root = API_BASE.split("?")[0].split("&")[0] || "/index.php";
    url = `${root}?s=${encodeURIComponent(`${tpUserPrefix}/${action}`)}`;
  } else if (/\/index\.php$/i.test(API_BASE.replace(/[#].*$/, "")) && API_BASE.indexOf("UserApi") !== -1) {
    const root = API_BASE.indexOf("?") !== -1 ? API_BASE.slice(0, API_BASE.indexOf("?")) : API_BASE;
    url = `${root}?s=${encodeURIComponent(`${tpUserPrefix}/${action}`)}`;
  } else {
    url = `${API_BASE.replace(/\/+$/, "")}/${action}`;
  }
  const extraQuery = options.query && typeof options.query === "object" ? options.query : null;
  if (extraQuery) {
    Object.keys(extraQuery).forEach(key => {
      const v = extraQuery[key];
      if (v === undefined || v === null) return;
      url += (url.indexOf("?") === -1 ? "?" : "&") + encodeURIComponent(key) + "=" + encodeURIComponent(String(v));
    });
  }
  return fetch(url, {
    method: options.method || "GET",
    headers,
    body
  })
    .then(async res => {
      const text = await res.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch (e) {
        const snippet = String(text || "").slice(0, 160);
        throw new Error(`接口返回非 JSON（HTTP ${res.status}）：${snippet}`);
      }
      return data;
    })
    .then(res => {
      if (res.code !== 0) {
        const err = new Error(res.msg || "请求失败");
        err.apiCode = res.code;
        if (res.bizCode) err.bizCode = res.bizCode;
        throw err;
      }
      return res.data;
    });
}

export const userApi = {
  /** 无需登录：服务探活（DB、任务表最近更新时间、Redis 可选） */
  health() {
    return request("health");
  },
  login(payload) {
    return request("login", { method: "POST", body: payload, form: true });
  },
  sendLoginCode(payload) {
    return request("sendLoginCode", { method: "POST", body: payload, form: true });
  },
  profile() {
    return request("profile");
  },
  /** 无需登录：可积分购买的会员套餐（price_points>0） */
  vipPlans() {
    return request("vipPlans");
  },
  /** 积分购买会员（需登录） body: { planId } */
  buyVipPlan(body) {
    return request("buyVipPlan", { method: "POST", body, form: true });
  },
  /** 微信/支付宝购买会员（需登录） body: { vipPlanId, payType } */
  buyVipPlanPay(body) {
    return request("buyVipPlanPay", { method: "POST", body, form: true });
  },
  projects() {
    return request("projects");
  },
  deleteProject(id) {
    return request("deleteProject", { method: "POST", body: { id }, form: true });
  },
  createProject(body) {
    return request("createProject", { method: "POST", body, form: true });
  },
  updateProject(body) {
    return request("updateProject", { method: "POST", body, form: true });
  },
  /** 轻量：仅更新项目 meta_json（不要求 title/ratio/type） */
  updateProjectMetaJson(body) {
    return request("updateProjectMetaJson", { method: "POST", body, form: true });
  },
  /** 步骤一草稿保存（meta_json） */
  saveProjectDraft(body) {
    return request("saveProjectDraft", { method: "POST", body, form: true });
  },
  /** 单个项目详情（含 meta 草稿） */
  projectDetail(id) {
    // API_BASE 为 /index.php?s=index/UserApi 时，path 内不能含 ?id=，否则会把 ?id= 并入 s 参数导致「方法不存在」
    return request("projectDetail", { query: { id } });
  },
  assets() {
    return request("assets");
  },
  uploadAsset({ file, assetType, name }) {
    const form = new FormData();
    form.append("file", file);
    if (assetType) form.append("assetType", assetType);
    if (name) form.append("name", name);
    return request("uploadAsset", { method: "POST", body: form, formData: true });
  },
  updateAsset(payload) {
    return request("updateAsset", { method: "POST", body: payload, form: true });
  },
  deleteAsset(id) {
    return request("deleteAsset", { method: "POST", body: { id }, form: true });
  },
  /** 将资产库图片同步到 SD2 官转素材库，写入 volc_asset_id */
  syncAssetVolc(id, waitActive = true) {
    return request("syncAssetVolc", {
      method: "POST",
      body: { id, waitActive: waitActive ? 1 : 0 },
      form: true
    });
  },
  /** 支付订单（`fa_ai_recharge_order`）分页；query: { offset, limit, type: all|recharge|vip } */
  orders(query = {}) {
    const q = {};
    if (query.offset != null) q.offset = query.offset;
    if (query.limit != null) q.limit = query.limit;
    if (query.type) q.type = query.type;
    return Object.keys(q).length ? request("orders", { query: q }) : request("orders");
  },
  /** 积分流水；query: { offset, limit }，返回 { records, hasMore, nextOffset, ... } */
  pointsRecords(query = {}) {
    const q = {};
    if (query.offset != null) q.offset = query.offset;
    if (query.limit != null) q.limit = query.limit;
    return Object.keys(q).length ? request("pointsRecords", { query: q }) : request("pointsRecords");
  },
  /** 无需登录 */
  rechargePlans() {
    return request("rechargePlans");
  },
  /** 无需登录：画幅、类型、画面风格 */
  setupDictionary() {
    return request("setupDictionary");
  },
  /** 无需登录：视频生成模型（时长/分辨率来自后台 fa_ai_model） */
  videoModels() {
    return request("videoModels");
  },
  /** 无需登录：文字模型（fa_ai_model.model_type=text），步骤一 */
  textModels() {
    return request("textModels");
  },
  /** 无需登录：图片模型（fa_ai_model.model_type=image），步骤二 */
  imageModels() {
    return request("imageModels");
  },
  /** 步骤一：异步任务创建 */
  createGenerateProjectStep1Task(body) {
    return request("createGenerateProjectStep1Task", { method: "POST", body, form: true });
  },
  /** 步骤一：单独刷新子步骤 1~5（subStep 必填；subStep=5 时可传 episodeNo 仅刷新单集） */
  refreshProjectStep1SubStep(body) {
    return request("refreshProjectStep1SubStep", { method: "POST", body, form: true });
  },
  /** 清空步骤一～三落库数据，保留剧本正文，用于重新生成前重置 */
  resetProjectForStep1Regenerate(body) {
    return request("resetProjectForStep1Regenerate", { method: "POST", body, form: true });
  },
  /** 步骤一：异步任务轮询 */
  getGenerateProjectStep1TaskStatus(body) {
    return request("getGenerateProjectStep1TaskStatus", { method: "POST", body, form: true });
  },
  /** 步骤三：由剧本生成分集列表占位（占位实现） */
  generateProjectStep3Storyboards(body) {
    return request("generateProjectStep3Storyboards", { method: "POST", body, form: true });
  },
  /** 步骤二：创建实体预览图异步任务（需 worker 消费） */
  generateEntityImage(body) {
    return request("generateEntityImage", { method: "POST", body, form: true });
  },
  /** 步骤二：轮询实体预览图任务状态 */
  getGenerateEntityImageTaskStatus(body) {
    return request("getGenerateEntityImageTaskStatus", { method: "POST", body, form: true });
  },
  /** 步骤二：当前项目未完成的实体预览任务（pending/running），刷新后恢复队列 */
  listStep2EntityImageTasksForProject(body) {
    return request("listStep2EntityImageTasksForProject", { method: "POST", body, form: true });
  },
  /** 步骤二：轻量保存单个实体（避免大 metaJson） */
  saveProjectEntity(body) {
    return request("saveProjectEntity", { method: "POST", body, form: true });
  },
  /** 步骤四：生成/重新生成单个分镜视频片段 */
  generateStoryboardVideo(body) {
    return request("generateStoryboardVideo", { method: "POST", body, form: true });
  },
  /** 步骤四：创建异步视频生成任务 */
  createGenerateStoryboardVideoTask(body) {
    return request("createGenerateStoryboardVideoTask", { method: "POST", body, form: true });
  },
  /** 步骤四：保存当前分镜提示词 */
  saveStoryboardPrompt(body) {
    return request("saveStoryboardPrompt", { method: "POST", body, form: true });
  },
  /** 步骤四：保存当前分镜视频参数（时长/分辨率） */
  saveStoryboardVideoConfig(body) {
    return request("saveStoryboardVideoConfig", { method: "POST", body, form: true });
  },
  /** 步骤四：查询异步视频生成任务状态 */
  getGenerateStoryboardVideoTaskStatus(body) {
    return request("getGenerateStoryboardVideoTaskStatus", { method: "POST", body, form: true });
  },
  /** 步骤四：列出项目下进行中的视频生成任务 */
  listStep4VideoTasks(body) {
    return request("listStep4VideoTasks", { method: "POST", body, form: true });
  },
  /** 视频快速创作：创建任务 */
  createQuickVideoTask(body) {
    return request("createQuickVideoTask", { method: "POST", body, form: true });
  },
  /** 视频快速创作：轮询状态 */
  getQuickVideoTaskStatus(body) {
    return request("getQuickVideoTaskStatus", { method: "POST", body, form: true });
  },
  /** 视频快速创作：历史列表 */
  listQuickVideoJobs(body) {
    return request("listQuickVideoJobs", { method: "POST", body, form: true });
  },
  /** 分镜页：将资产库图片绑定到分集 */
  bindEpisodeAssets(body) {
    return request("bindEpisodeAssets", { method: "POST", body, form: true });
  },
  /** 分镜页：将本项目实体（fa_ai_project_entity）绑定到分集 */
  bindEpisodeProjectEntities(body) {
    return request("bindEpisodeProjectEntities", { method: "POST", body, form: true });
  },
  /** 双链路·剧本阶段：按当前剧本正文 AI 预解析并写入分集-实体预绑定（auto_script），可与分镜 @ 校准并存 */
  scriptDramaParseBindings(body) {
    return request("scriptDramaParseBindings", { method: "POST", body, form: true });
  },
  /** body: { planId } 或 { points } */
  recharge(body) {
    return request("recharge", { method: "POST", body, form: true });
  },
  queryRechargeOrder(body) {
    return request("queryRechargeOrder", { method: "POST", body, form: true });
  },
  rechargePayNotify(body) {
    return request("rechargePayNotify", { method: "POST", body, form: true });
  }
};
